import { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { toast } from "sonner";

/**
 * Tiny admin trash button used in any signup/waitlist table. Confirms
 * once via AlertDialog, then calls `onConfirm` and lets the parent
 * patch its own local state. Errors surface as a toast so the row
 * stays visible if the API call fails.
 */
const DeleteRowButton = ({ testid, email, label = "entry", onConfirm }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await onConfirm?.();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not delete ${label}.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid={testid}
          aria-label={`Delete ${email}`}
          className="text-[#4a6984]/60 hover:text-rose-600 hover:bg-rose-50 h-8 w-8 p-0"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1c2d3d]">
            Delete this {label}?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#4a6984]">
            <span className="font-bold text-[#1c2d3d]">{email}</span> will be
            permanently removed. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handle}
            disabled={busy}
            data-testid={`${testid}-confirm`}
            className="bg-rose-600 hover:bg-rose-700 text-white font-extrabold"
          >
            {busy ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting…</>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteRowButton;
